import { useState, useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation as useRouterLocation, Navigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Activity, Settings, Database, LogOut, User, Shield, Loader2, MapPin, Layers, ListChecks, LayoutDashboard, Building2, FileText, KeyRound, Lock, Mail } from "lucide-react";
import ChangePasswordDialog from "./components/ChangePasswordDialog";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LocationProvider, useLocation } from "@/contexts/LocationContext";
import { GlobalLocationFilter } from "@/components/GlobalLocationFilter";
import SyncHealthPulse from "@/components/SyncHealthPulse";
import CronFailureAlert from "@/components/CronFailureAlert";
import NoAccess from "@/pages/NoAccess";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import SmtpConfigs from "@/pages/SmtpConfigs";
import Register from "@/pages/Register";

// Lazy load heavy pages for faster initial load
const AdminSettings = lazy(() => import("@/pages/AdminSettings"));
const EmailDigest = lazy(() => import("@/pages/EmailDigest"));
const EmailDigestAndLogins = lazy(() => import("@/pages/EmailDigestAndLogins"));
const TechGuide = lazy(() => import("@/pages/TechGuide"));
const DataExplorer = lazy(() => import("@/pages/DataExplorer"));
const SyncAudit = lazy(() => import("@/pages/SyncAudit"));
const SyncJobs = lazy(() => import("@/pages/SyncJobs"));
const SyncCenter = lazy(() => import("@/pages/SyncCenter"));
const SettingsHub = lazy(() => import("@/pages/SettingsHub"));
const SalesGrowth = lazy(() => import("@/pages/SalesGrowth"));
const Clinic = lazy(() => import("@/pages/Clinic"));
const Executive = lazy(() => import("@/pages/Executive"));
const ExecutiveKpiDrilldown = lazy(() => import("@/pages/ExecutiveKpiDrilldown"));
const EmbedClinician = lazy(() => import("@/pages/EmbedClinician"));
const LocationManager = lazy(() => import("@/pages/LocationManager"));
const SegmentManager = lazy(() => import("@/pages/SegmentManager"));
const UserManager = lazy(() => import("@/pages/UserManager"));
const SnapshotManager = lazy(() => import("@/pages/SnapshotManager"));
const KpiDrilldown = lazy(() => import("@/pages/KpiDrilldown"));
const SyncLogs = lazy(() => import("@/pages/SyncLogs"));
const DailySync = lazy(() => import("@/pages/DailySync"));
const CRMPractitionerPins = lazy(() => import("@/pages/CRMPractitionerPins"));
const PatientIntelligence = lazy(() => import("@/pages/PatientIntelligence"));
const LocationSummary = lazy(() => import("@/pages/LocationSummary"));
const Charting = lazy(() => import("@/pages/Charting"));
const Scoreboard = lazy(() => import("@/pages/Scoreboard"));
const MonthlyDrilldown = lazy(() => import("@/pages/MonthlyDrilldown"));
const HQWeeklyReport = lazy(() => import("@/pages/HQWeeklyReport"));

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (!isAdmin()) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

const Navigation = () => {
  const location = useRouterLocation();
  const currentPath = location.pathname;
  const { user, logout, isAdmin, hasPermission, canAccessSyncCenter } = useAuth();
  const [changePwOpen, setChangePwOpen] = useState(false);

  // Load UI preferences
  const [uiPrefs, setUiPrefs] = useState({
    showExecutiveReport: true,
    showSalesReport: true,
    showClinicReport: true
  });

  useEffect(() => {
    const savedPreferences = localStorage.getItem('uiPreferences');
    if (savedPreferences) {
      setUiPrefs(JSON.parse(savedPreferences));
    }
  }, []);

  const getInitials = () => {
    if (!user) return '?';
    return `${user.first_name?.charAt(0) || ''}${user.last_name?.charAt(0) || ''}`.toUpperCase();
  };

  // Don't show navigation on KPI drilldown pages
  const isKpiPage = currentPath.startsWith('/kpi/');

  return (
    <div className="sticky top-0 z-40 border-b bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 max-w-[1440px]">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-3">
              <img src="/optsc-logo.png" alt="OPTSC" className="w-8 h-8 rounded" />
              <div>
                <h1 className="text-xl font-semibold tracking-tight">Ottawa Physiotherapy</h1>
                <p className="text-xs text-muted-foreground">OPTSC Reporting Dashboard</p>
              </div>
            </Link>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Sync health pulse — hidden when no active syncs */}
            <SyncHealthPulse />

            {/* Critical cron failure alert — hidden when no consecutive failures */}
            <CronFailureAlert />

            {/* Global Location Filter - Hidden on Scoreboard page */}
            {!currentPath.startsWith('/scoreboard') && <GlobalLocationFilter />}
            
            {!isKpiPage && (
              <Tabs value={currentPath} className="w-auto">
                <TabsList>
                  {hasPermission('report_executive') && uiPrefs.showExecutiveReport && (
                    <Link to="/">
                      <TabsTrigger data-testid="nav-tab-executive" value="/" className="gap-2">
                        Executive
                      </TabsTrigger>
                    </Link>
                  )}
                  {hasPermission('report_sales') && uiPrefs.showSalesReport && (
                    <Link to="/sales">
                      <TabsTrigger data-testid="nav-tab-sales" value="/sales" className="gap-2">
                        Sales Growth
                      </TabsTrigger>
                    </Link>
                  )}
                  {hasPermission('report_clinic') && uiPrefs.showClinicReport && (
                    <Link to="/clinic">
                      <TabsTrigger data-testid="nav-tab-clinic" value="/clinic" className="gap-2">
                        Clinic (CRM)
                      </TabsTrigger>
                    </Link>
                  )}
                  {hasPermission('report_scoreboard') && (
                  <Link to="/scoreboard">
                    <TabsTrigger data-testid="nav-tab-scoreboard" value="/scoreboard" className="gap-2">
                      <LayoutDashboard className="w-4 h-4" />
                      Scoreboard
                    </TabsTrigger>
                  </Link>
                  )}
                  {hasPermission('report_hq') && (
                  <Link to="/hq/weekly-report">
                    <TabsTrigger data-testid="nav-tab-hq-report" value="/hq/weekly-report" className="gap-2">
                      <FileText className="w-4 h-4" />
                      HQ Report
                    </TabsTrigger>
                  </Link>
                  )}
                </TabsList>
              </Tabs>
            )}
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="relative h-9 w-9 rounded-full" data-testid="user-menu">
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="bg-primary/10 text-primary font-medium">
                      {getInitials()}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-xs leading-none text-muted-foreground">
                      {user?.email}
                    </p>
                    <Badge variant="secondary" className="w-fit mt-1 text-xs">
                      {user?.role === 'admin' ? (
                        <><Shield className="w-3 h-3 mr-1" /> Admin</>
                      ) : (
                        <><User className="w-3 h-3 mr-1" /> Viewer</>
                      )}
                    </Badge>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {hasPermission('data_explorer') && (
                  <Link to="/data">
                    <DropdownMenuItem data-testid="menu-data-explorer">
                      <Database className="mr-2 h-4 w-4" />
                      Data Explorer
                    </DropdownMenuItem>
                  </Link>
                )}
                {canAccessSyncCenter() && (
                  <Link to="/sync-center">
                    <DropdownMenuItem data-testid="menu-sync-center">
                      <Activity className="mr-2 h-4 w-4" />
                      Sync Center
                    </DropdownMenuItem>
                  </Link>
                )}
                {hasPermission('report_executive') && (
                  <Link to="/email-digest">
                    <DropdownMenuItem data-testid="menu-email-digest">
                      <Mail className="mr-2 h-4 w-4" />
                      Clinicians & Digest
                    </DropdownMenuItem>
                  </Link>
                )}
                <Link to="/settings">
                  <DropdownMenuItem data-testid="menu-settings">
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                </Link>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setChangePwOpen(true)} data-testid="menu-change-password">
                  <KeyRound className="mr-2 h-4 w-4" />
                  Change Password
                </DropdownMenuItem>
                <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      <ChangePasswordDialog open={changePwOpen} onOpenChange={setChangePwOpen} />
    </div>
  );
};

const AppContent = () => {
  const { isAuthenticated, loading, user, refreshUser, hasPermission, canAccessSyncCenter } = useAuth();
  const [forcedChangeOpen, setForcedChangeOpen] = useState(false);

  // Auto-open the forced Change Password dialog when the user has must_change_password.
  // Rules:
  //   - Only fire when the user is authenticated and the flag is true.
  //   - After a successful change, refreshUser() will flip the flag → dialog closes.
  //   - The dialog cannot be dismissed while forced=true (see ChangePasswordDialog).
  useEffect(() => {
    if (isAuthenticated && user?.must_change_password) {
      setForcedChangeOpen(true);
    } else {
      setForcedChangeOpen(false);
    }
  }, [isAuthenticated, user?.must_change_password]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="App min-h-screen bg-background">
      {/* Forced password change — top-level guard rendered above ALL routes so
          it applies regardless of where the user is navigated to after login. */}
      {isAuthenticated && (
        <ChangePasswordDialog
          open={forcedChangeOpen}
          onOpenChange={setForcedChangeOpen}
          forced
          onSuccess={refreshUser}
        />
      )}
      <Routes>
        {/* Public embed route - no auth required */}
        <Route path="/embed/clinician" element={<EmbedClinician />} />
        
        <Route path="/login" element={
          isAuthenticated ? <Navigate to="/" replace /> : <Login />
        } />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/settings/smtp" element={<SmtpConfigs />} />
        <Route path="/register" element={
          isAuthenticated ? <Navigate to="/" replace /> : <Register />
        } />
        <Route path="/*" element={
          <ProtectedRoute>
            <LocationProvider>
              <>
                <Navigation />
                <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>}>
                <Routes>
                  <Route
                    path="/"
                    element={
                      hasPermission('report_executive') ? (
                        <Executive />
                      ) : hasPermission('report_scoreboard') ? (
                        <Navigate to="/scoreboard" replace />
                      ) : hasPermission('report_sales') ? (
                        <Navigate to="/sales" replace />
                      ) : hasPermission('report_clinic') ? (
                        <Navigate to="/clinic" replace />
                      ) : hasPermission('report_hq') ? (
                        <Navigate to="/hq/weekly-report" replace />
                      ) : hasPermission('data_explorer') ? (
                        <Navigate to="/data" replace />
                      ) : (
                        <NoAccess />
                      )
                    }
                  />
                  {hasPermission('report_sales') && (
                    <Route path="/sales" element={<SalesGrowth />} />
                  )}
                  {hasPermission('report_clinic') && (
                    <Route path="/clinic" element={<Clinic />} />
                  )}
                  {hasPermission('data_explorer') && (
                    <Route path="/data" element={<DataExplorer />} />
                  )}
                  {/* Sync Center — unified hub (replaces /sync-audit, /sync-jobs, /admin/logs, /admin/daily-sync, /admin/snapshots) */}
                  {canAccessSyncCenter() && (
                    <Route path="/sync-center" element={<SyncCenter />} />
                  )}
                  {/* Backward-compatible aliases — redirect to the new unified Sync Center tab */}
                  {canAccessSyncCenter() && (
                    <Route path="/sync-audit" element={<Navigate to="/sync-center?tab=daily-stats" replace />} />
                  )}
                  {canAccessSyncCenter() && (
                    <Route path="/sync-jobs" element={<Navigate to="/sync-center?tab=live" replace />} />
                  )}

                  <Route path="/scoreboard" element={<Scoreboard />} />
                  <Route path="/scoreboard/monthly/:year/:month" element={<MonthlyDrilldown />} />
                  <Route path="/hq/weekly-report" element={<HQWeeklyReport />} />

                  {/* Settings hub (replaces /admin/users, /admin/locations, /admin/segments, /admin/crm-pins) */}
                  <Route path="/settings" element={<SettingsHub />} />
                  {/* Email Digest & Logins — standalone top-level page */}
                  {hasPermission('report_executive') && (
                    <Route path="/email-digest" element={<EmailDigestAndLogins />} />
                  )}
                  {/* Backward-compatible aliases */}
                  <Route path="/settings/email-digest" element={<Navigate to="/email-digest" replace />} />
                  <Route path="/tech-guide" element={<TechGuide />} />

                  {/* KPI drilldowns are permissioned on the backend; route stays available for share links */}
                  <Route path="/kpi/:kpiName" element={<KpiDrilldown />} />
                  {hasPermission('report_executive') && (
                    <Route path="/reports/executive/:kpiName" element={<ExecutiveKpiDrilldown />} />
                  )}

                  {/* Backward-compatible admin route aliases — redirect to Settings tabs */}
                  {hasPermission('manage_users') && (
                    <Route path="/admin/users" element={<Navigate to="/settings?tab=users" replace />} />
                  )}
                  {hasPermission('manage_locations') && (
                    <Route path="/admin/locations" element={<Navigate to="/settings?tab=locations" replace />} />
                  )}
                  {canAccessSyncCenter() && (
                    <Route path="/admin/snapshots" element={<Navigate to="/sync-center?tab=snapshots" replace />} />
                  )}
                  {hasPermission('manage_segments') && (
                    <Route path="/admin/segments" element={<Navigate to="/settings?tab=franchise" replace />} />
                  )}
                  {canAccessSyncCenter() && (
                    <Route path="/admin/logs" element={<Navigate to="/sync-center?tab=history" replace />} />
                  )}
                  {canAccessSyncCenter() && (
                    <Route path="/admin/daily-sync" element={<Navigate to="/sync-center?tab=live" replace />} />
                  )}
                  {hasPermission('manage_locations') && (
                    <Route path="/admin/crm-pins" element={<Navigate to="/email-digest?section=crm-pins" replace />} />
                  )}
                  {hasPermission('report_executive') && (
                    <>
                      <Route path="/patient-intelligence" element={<PatientIntelligence />} />
                      <Route path="/location-summary" element={<LocationSummary />} />
                    </>
                  )}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                </Suspense>
              </>
            </LocationProvider>
          </ProtectedRoute>
        } />
      </Routes>
      <Toaster />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
